// Tournament games catalog.
//
// Deliberately SEPARATE from the player-side gamesCatalog. Tournaments only
// offer the games that work across groups of 3–4 (the per-hole live-pick games
// like Wolf / Bingo / Oosler don't), and a few games carry tournament-specific
// option sets (Skins total-pot mode, Stableford best-net, team games). Keeping
// this list independent means the player-side modal is untouched.
//
// Reuses the GameDefinition / GameOption shapes so the options editor and the
// bet grid render the same way they do on the player side.

import {
  GameDefinition,
  GameOption,
} from "./gamesCatalog";

// Shared bet-mode block (Bet per Player / Bet per Point + Nassau segment bets),
// rendered by TournamentBetGrid. Defaults differ per game (e.g. Stroke Play
// defaults everything to 0 so the leaderboard runs without money).
function betGridOptions(opts: {
  overall: number;
  perPoint: number;
  segment: number;
}): GameOption[] {
  return [
    {
      key: "overallBet",
      type: "number",
      default: opts.overall,
      label: "Bet per Player",
      min: 0,
      zeroLabel: "—",
      advanced: true,
    },
    {
      key: "betPerPoint",
      type: "number",
      default: opts.perPoint,
      label: "Bet per Point",
      min: 0,
      zeroLabel: "—",
      advanced: true,
      dependsOn: { key: "nassau", value: false },
    },
    {
      key: "useBetPerPoint",
      type: "boolean",
      default: false,
      label: "Use bet per point instead of bet per player",
      advanced: true,
      dependsOn: { key: "nassau", value: false },
    },
    {
      key: "frontNineBet",
      type: "number",
      default: opts.segment,
      label: "Front 9 bet",
      min: 0,
      zeroLabel: "—",
      advanced: true,
      dependsOn: { key: "nassau", value: true },
    },
    {
      key: "backNineBet",
      type: "number",
      default: opts.segment,
      label: "Back 9 bet",
      min: 0,
      zeroLabel: "—",
      advanced: true,
      dependsOn: { key: "nassau", value: true },
    },
    {
      key: "nassau",
      type: "boolean",
      default: false,
      label: "Nassau (front 9 + back 9 sub-bets)",
    },
  ];
}

const grossNetRadio: GameOption = {
  key: "scoringMode",
  type: "select",
  default: "net",
  label: "Score basis",
  choices: [
    { value: "gross", label: "Gross" },
    { value: "net", label: "Net" },
  ],
  renderAs: "radio",
};

const loneRanger: GameOption = {
  key: "loneRanger",
  type: "boolean",
  default: false,
  label: "Lone Ranger",
};

// Added to every tournament game. When on, the group collected the entry
// fee / bet up front, so the leaderboard shows gross payouts from the pot
// rather than net settle-up amounts.
const entryFeePaid: GameOption = {
  key: "entryFeePaid",
  type: "boolean",
  default: false,
  label: "Entry Fee Paid",
  info: "When Entry Fee Paid is on, the Leaderboard will reflect total amount owed to player. If it is off, total due to player will adjust to build in the player's own bets due.",
};

export const TOURNAMENT_GAMES: GameDefinition[] = [
  {
    id: "strokePlay",
    name: "Stroke Play",
    category: "individual",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    mandatory: true,
    description:
      "Always on for the leaderboard — lowest total strokes wins. Bets default to zero; set a Bet per Player / Bet per Point (or Nassau segments) under the gear if you want money on it.",
    options: [
      ...betGridOptions({ overall: 0, perPoint: 0, segment: 0 }),
      entryFeePaid,
    ],
  },
  {
    id: "skins",
    name: "Skins",
    category: "side",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    description:
      "Lowest outright score on each hole wins the skin. In pot mode set either a bet per player or a fixed total pot (gross and net scored in parallel). In carry-forward mode each hole has its own bet that rolls into the next on a tie.",
    options: [
      {
        key: "mode",
        type: "select",
        default: "pot",
        label: "Mode",
        choices: [
          { value: "pot", label: "Pot" },
          { value: "carryForward", label: "Carry forward" },
        ],
        renderAs: "radio",
      },
      // Pot sub-mode: bet per player vs a fixed total pot. Rendered by the
      // custom SkinsBetGrid; defined here so defaults seed.
      {
        key: "potMode",
        type: "select",
        default: "perPlayer",
        label: "Pot basis",
        choices: [
          { value: "perPlayer", label: "Bet per player" },
          { value: "totalPot", label: "Total pot" },
        ],
        dependsOn: { key: "mode", value: "pot" },
      },
      { key: "potBetGross", type: "number", default: 5, label: "Player bet (gross)", min: 0, dependsOn: { key: "mode", value: "pot" } },
      { key: "potBetNet", type: "number", default: 5, label: "Player bet (net)", min: 0, dependsOn: { key: "mode", value: "pot" } },
      { key: "potTotalGross", type: "number", default: 0, label: "Total pot (gross)", min: 0, dependsOn: { key: "mode", value: "pot" } },
      { key: "potTotalNet", type: "number", default: 0, label: "Total pot (net)", min: 0, dependsOn: { key: "mode", value: "pot" } },
      { key: "carryBetGross", type: "number", default: 1, label: "Bet per hole (gross)", min: 0, dependsOn: { key: "mode", value: "carryForward" } },
      { key: "carryBetNet", type: "number", default: 1, label: "Bet per hole (net)", min: 0, dependsOn: { key: "mode", value: "carryForward" } },
      entryFeePaid,
    ],
  },
  {
    id: "stableford",
    name: "Stableford",
    category: "individual",
    scoringType: "points",
    requiresHandicap: true,
    requiresTeams: false,
    description:
      "Net score vs net par yields per-hole points (full handicap). Set a Bet per Player or Bet per Point, optionally Nassau, and how many best net scores count per group.",
    options: [
      ...betGridOptions({ overall: 10, perPoint: 1, segment: 5 }),
      {
        key: "bestNet",
        type: "select",
        default: "1",
        label: "Best net scores to count",
        choices: [
          { value: "1", label: "1" },
          { value: "2", label: "2" },
          { value: "3", label: "3" },
          { value: "4", label: "4" },
        ],
      },
      {
        key: "scoringScale",
        type: "select",
        default: "standard",
        label: "Points scale",
        choices: [
          { value: "standard", label: "Standard: 1 bogey / 2 par / 3 birdie / 4 eagle / 5 albatross" },
          { value: "modified", label: "Modified: 1 dbl-bogey / 2 bogey / 3 par / 4 birdie / 5 eagle / 6 albatross" },
          { value: "modifiedPga", label: "Modified (PGA): -1 bogey / 0 par / +2 birdie / +5 eagle / +8 albatross" },
        ],
      },
      loneRanger,
      entryFeePaid,
    ],
  },
  {
    id: "alliance",
    name: "Alliance (Bowmaker)",
    category: "team",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: true,
    description:
      "Best 3 net scores count on Par 3s, best 2 on Par 4s, and single best on Par 5s. The team posting the lowest combined net score wins.",
    options: [
      ...betGridOptions({ overall: 10, perPoint: 1, segment: 5 }),
      grossNetRadio,
      entryFeePaid,
    ],
  },
  {
    id: "chaChaCha",
    name: "Cha Cha Cha",
    category: "team",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: true,
    description:
      "1 best score counts on the first hole, 2 best on the second, and 3 best on the third, repeating every three holes. The team posting the lowest combined net score wins. Turn on Lone Ranger to count designated players' scores on their assigned holes (e.g. player A counts on 1, 5, 9, 13, 17 regardless of whether they're best).",
    options: [
      ...betGridOptions({ overall: 10, perPoint: 1, segment: 5 }),
      grossNetRadio,
      loneRanger,
      entryFeePaid,
    ],
  },
];

// Games that render the Bet per Player / Bet per Point grid (shared with the
// player-side StablefordBetGrid pattern).
export const TOURNAMENT_BET_GRID_IDS = new Set([
  "strokePlay",
  "stableford",
  "alliance",
  "chaChaCha",
]);

// Option keys owned by the bet grid — the generic option loop skips these.
export const TOURNAMENT_BET_GRID_KEYS = new Set([
  "overallBet",
  "betPerPoint",
  "useBetPerPoint",
  "frontNineBet",
  "backNineBet",
]);

// Skins owns these via its custom pot/carry grid.
export const SKINS_OPTION_KEYS = new Set([
  "mode",
  "potMode",
  "potBetGross",
  "potBetNet",
  "potTotalGross",
  "potTotalNet",
  "carryBetGross",
  "carryBetNet",
]);

export const TOURNAMENT_MANDATORY_IDS = TOURNAMENT_GAMES.filter(
  (g) => g.mandatory,
).map((g) => g.id);

export function getTournamentGame(id: string): GameDefinition | undefined {
  return TOURNAMENT_GAMES.find((g) => g.id === id);
}

export function tournamentGameName(id: string): string {
  return getTournamentGame(id)?.name ?? id;
}

export function tournamentDefaultOptionsFor(
  id: string,
): Record<string, boolean | number | string> {
  const def = getTournamentGame(id);
  if (!def) return {};
  const out: Record<string, boolean | number | string> = {};
  for (const opt of def.options) out[opt.key] = opt.default;
  return out;
}
